/**
 * Bar Stock Material Combobox
 *
 * Autocomplete picker for bar stock (棒料) materials. Searches across ALL
 * active bar stock products (not limited to a work order's BOM) so leftover
 * registration can reflect what was actually used.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { debounce } from 'lodash';
import barStockService from '../../services/barStockService';

function BarStockMaterialCombobox({
  value = '',
  displayName = '',
  onSelect,
  disabled = false,
  placeholder = '搜索棒料编码、名称、规格...'
}) {
  const [open, setOpen] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');

  const searchMaterials = useCallback(async (searchText) => {
    try {
      setLoading(true);
      const result = await barStockService.searchBarStockMaterials(searchText || '');
      setMaterials(result.materials || []);
    } catch (err) {
      console.error('搜索棒料失败:', err);
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(searchMaterials, 300),
    [searchMaterials]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Load an initial batch whenever the dropdown opens
  useEffect(() => {
    if (open) {
      searchMaterials('');
    }
  }, [open, searchMaterials]);

  // Clear the cached label when the parent clears the value
  useEffect(() => {
    if (!value) setSelectedLabel('');
  }, [value]);

  const handleSelect = (material) => {
    setSelectedLabel(`${material.product_code}${material.name ? ` - ${material.name}` : ''}`);
    onSelect({
      code: material.product_code,
      name: material.name || '',
      id: material.product_id || '',
      specification: material.specification || '',
      tip_diameter: material.tip_diameter || 0,
      total_length: material.total_length || 0,
      unit: material.unit || '根'
    });
    setOpen(false);
  };

  const triggerLabel = selectedLabel || (value ? `${value}${displayName ? ` - ${displayName}` : ''}` : placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-8"
          disabled={disabled}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="输入棒料编码、名称或规格..."
            onValueChange={debouncedSearch}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? '搜索中...' : '未找到棒料'}
            </CommandEmpty>
            <CommandGroup>
              {materials.map((material) => (
                <CommandItem
                  key={material.product_id}
                  value={material.product_code}
                  onSelect={() => handleSelect(material)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === material.product_code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {material.product_code}
                      {material.name ? ` - ${material.name}` : ''}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {[
                        material.specification,
                        material.tip_diameter ? `刃径${material.tip_diameter}mm` : null,
                        material.total_length ? `长度${material.total_length}mm` : null
                      ].filter(Boolean).join(' | ')}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

BarStockMaterialCombobox.propTypes = {
  value: PropTypes.string,
  displayName: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
};

export default BarStockMaterialCombobox;
